import { useTranslation } from 'react-i18next';

import { PurchasePaymentStatus } from '__generated__/graphql';
import { encodeUrl } from '../../common/helpers';
import EventPicker from '../Events/EventPicker';
import GetRevenueReportQuery from './GetRevenueReportQuery';
import UI from '../../common/UI';
import config from '../../config';
import useLocale from '../../common/useLocale';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useUrlState from '../../common/useUrlState';

const RevenueReport = () => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const project = useProject();

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { string }) => ({
      ...params,
      event: string(params.event),
      start_date: string(params.start_date),
      end_date: string(params.end_date),
    }),
  });

  const eventId = urlState.event;
  const startDate = urlState.start_date;
  const endDate = urlState.end_date;

  const filters = {
    events: eventId ? [eventId] : null,
    start_date: startDate,
    end_date: endDate,
  };

  const { data, loading } = useQuery(GetRevenueReportQuery, {
    variables: {
      project: project.id,
      ...filters,
    },
  });

  if (!data) {
    return <UI.PageLoader />;
  }

  const stats = data.project.revenue_stats;

  return (
    <UI.GridContainer>
      <UI.FormGrid>
        <UI.InputGroup>
          <UI.InputLabel>
            {t('event')}
          </UI.InputLabel>
          <EventPicker
            eventId={eventId}
            onChange={(event) => setUrlState((params) => ({ ...params, event: event?.id }))}
          />
        </UI.InputGroup>
        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup>
            <UI.InputLabel htmlFor="start_date">
              {t('start_date')}
            </UI.InputLabel>
            <UI.DatePicker
              selected={startDate}
              onChange={(value) => setUrlState((params) => ({ ...params, start_date: value }))}
              selectsStart
              id="start_date"
            />
          </UI.InputGroup>
          <UI.InputGroup>
            <UI.InputLabel htmlFor="end_date">
              {t('end_date')}
            </UI.InputLabel>
            <UI.DatePicker
              selected={endDate}
              onChange={(value) => setUrlState((params) => ({ ...params, end_date: value }))}
              selectsStart
              id="end_date"
            />
          </UI.InputGroup>
        </UI.FormGrid>
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.AButton
            href={`${config.backendPath}/api/revenue/${project.id}/csv?${encodeUrl(filters)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('export')}
          </UI.AButton>
        </UI.Div>
      </UI.FormGrid>
      <UI.HR />
      <UI.Div sc={{ muted: loading }}>
        <UI.Legend sc={{ mb: 2 }}>{t('totals')}</UI.Legend>
        <UI.Table sc={{ spacingV: 1, borderTop: true, borderBottom: true }}>
          <UI.TBody>
            <UI.TR>
              <UI.TD style={{ width: '50%' }}>
                <UI.Link
                  to={`/${project.id}/participants?${encodeUrl({
                    event: eventId,
                    payment_statuses: [PurchasePaymentStatus.paid],
                  })}`}
                  sc={{ secondary: true }}
                >
                  {t('paid')}
                </UI.Link>
              </UI.TD>
              <UI.TD sc={{ textAlign: 'right' }}>
                {formatCurrency(stats.paid_amount)}
              </UI.TD>
            </UI.TR>
            <UI.TR>
              <UI.TD>
                <UI.Link
                  to={`/${project.id}/participants?${encodeUrl({
                    event: eventId,
                    payment_statuses: [PurchasePaymentStatus.refunded],
                  })}`}
                  sc={{ secondary: true }}
                >
                  {t('refunded')}
                </UI.Link>
              </UI.TD>
              <UI.TD sc={{ textAlign: 'right' }}>
                {formatCurrency(-stats.refunded_amount)}
              </UI.TD>
            </UI.TR>
          </UI.TBody>
          <UI.TBody>
            <UI.TR sc={{ strong: true }}>
              <UI.TD>
                {t('revenue')}
              </UI.TD>
              <UI.TD sc={{ textAlign: 'right' }}>
                {formatCurrency(stats.paid_amount - stats.refunded_amount)}
              </UI.TD>
            </UI.TR>
          </UI.TBody>
        </UI.Table>
      </UI.Div>
    </UI.GridContainer>
  );
};

export default RevenueReport;
